import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CartItem from "../components/CartItem";
import OrderSummary from "../components/cart/OrderSummary";
import ResultModal from "../components/cart/ResultModal";
import useCartQuote from "../hooks/useCartQuote";
import {
  Button,
  EmptyState,
  Input,
  OptionCard,
  RadioDot,
  Select,
  TextArea,
} from "../components/ui";
import { formatZl } from "../utils/format";
import { cartLines, trackEvent } from "../analytics";

import {
  checkout,
  fetchPaymentMethods,
  fetchDeliveryMethods,
  resetOrderSuccess,
} from "../store/features/cartSlice";
import { fetchAddresses } from "../store/features/addressesSlice";

import {
  UserIcon,
  TruckIcon,
  CreditCardIcon,
  ChevronRightIcon,
  ShoppingBagIcon,
  ArrowLeftIcon,
} from "@heroicons/react/24/outline";

const SectionTitle = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2 mb-4">
    <Icon className="w-5 h-5 text-blue-400" />
    <h2 className="text-lg font-bold">{children}</h2>
  </div>
);

const ERROR_TEXT = {
  name: "Введите ваше имя",
  phone: "Введите номер телефона",
  tg_username: "Введите Telegram username",
  email: "Введите email",
  address: "Введите адрес доставки",
  delivery_method: "Выберите способ доставки",
  payment_method: "Выберите способ оплаты",
};

const CartPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { items, paymentMethods, deliveryMethods, orderError, orderSuccess, orderNumber } =
    useSelector((state) => state.cart);
  const addresses = useSelector((state) => state.addresses.list);
  const user = useSelector((state) => state.auth.user);
  const isLoggedIn = Boolean(user);

  const [selectedSavedAddress, setSelectedSavedAddress] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    tg_username: "",
    email: "",
    comment: "",
    payment_method: "",
    delivery_method: "",
    address: "",
  });

  const [promoInput, setPromoInput] = useState("");
  const [appliedCode, setAppliedCode] = useState("");

  // Ціни, акції, доставка — рахує сервер за тими ж правилами, що й при оформленні
  const { quote } = useCartQuote(items, formData.delivery_method, appliedCode);

  const selectedDelivery = deliveryMethods.find((m) => String(m.id) === formData.delivery_method);

  // Порожній список у способу доставки = доступні всі способи оплати
  const allowedPaymentIds = useMemo(
    () =>
      selectedDelivery?.payment_methods?.length
        ? selectedDelivery.payment_methods.map(String)
        : null,
    [selectedDelivery]
  );
  const isPaymentAllowed = (id) => !allowedPaymentIds || allowedPaymentIds.includes(String(id));

  // Змінили доставку, а обрана оплата для неї недоступна — скидаємо вибір
  useEffect(() => {
    if (
      formData.payment_method &&
      allowedPaymentIds &&
      !allowedPaymentIds.includes(formData.payment_method)
    ) {
      setFormData((prev) => ({ ...prev, payment_method: "" }));
    }
  }, [formData.payment_method, allowedPaymentIds]);

  useEffect(() => {
    window.scrollTo(0, 0);
    dispatch(fetchPaymentMethods());
    dispatch(fetchDeliveryMethods());
  }, [dispatch]);

  useEffect(() => {
    if (addresses.length === 0) {
      dispatch(fetchAddresses());
    }
  }, [dispatch, addresses.length]);

  useEffect(() => {
    if (isLoggedIn) {
      setFormData((prev) => ({
        ...prev,
        name: user.first_name || "",
        tg_username: user.tg_username || "Нет юзернейма",
      }));
    }
  }, [isLoggedIn, user]);

  // Скидаємо блокування кнопки, коли прийшов результат запиту (успіх або помилка)
  useEffect(() => {
    if (orderSuccess || orderError) {
      setIsSubmitting(false);
    }
  }, [orderSuccess, orderError]);

  // Початок оформлення — перша взаємодія з формою (один раз за візит у кошик)
  const checkoutStarted = useRef(false);
  const markCheckoutStart = () => {
    if (checkoutStarted.current) return;
    checkoutStarted.current = true;
    trackEvent("InitiateCheckout", { cart: cartLines(items) });
  };

  const setField = (name, value) => {
    markCheckoutStart();
    // Прибираємо помилку, коли користувач виправляє поле
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: false }));
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleInputChange = (e) => setField(e.target.name, e.target.value);

  // Пропси для текстового поля форми: значення, обробник, текст помилки
  const fieldProps = (name) => ({
    name,
    value: formData[name],
    onChange: handleInputChange,
    error: errors[name] && ERROR_TEXT[name],
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isSubmitting) return; // захист від подвійного кліку
    markCheckoutStart();

    const finalAddress = formData.address || selectedSavedAddress;
    const newErrors = {};

    if (!formData.name?.trim()) newErrors.name = true;
    if (!formData.phone?.trim()) newErrors.phone = true;
    if (!isLoggedIn && !formData.tg_username?.trim()) newErrors.tg_username = true;
    if (!formData.delivery_method) newErrors.delivery_method = true;
    if (!formData.payment_method || !isPaymentAllowed(formData.payment_method)) {
      newErrors.payment_method = true;
    }
    if (selectedDelivery?.address_required && !finalAddress?.trim()) newErrors.address = true;
    if (selectedDelivery?.email_required && !formData.email?.trim()) newErrors.email = true;

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // Прокрутка до першого поля з помилкою (після рендеру з data-error)
      requestAnimationFrame(() => {
        document
          .querySelector("[data-error='true']")
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
      return;
    }

    setErrors({});
    setIsSubmitting(true);

    dispatch(
      checkout({
        ...formData,
        address: finalAddress,
        promo_code: appliedCode,
        items,
      })
    );
  };

  // Поки немає відповіді сервера — рахуємо локально без знижок
  const localSubtotal = items.reduce((total, item) => total + item.price * item.quantity, 0);
  const subtotal = quote ? Number(quote.subtotal) : localSubtotal;
  const discount = quote ? Number(quote.discount) : 0;
  const deliveryPrice = !selectedDelivery
    ? 0
    : quote
    ? Number(quote.delivery_price)
    : Number(selectedDelivery.price) || 0;
  const totalPrice = quote ? Number(quote.total) : subtotal + deliveryPrice;

  const quoteLines = Object.fromEntries((quote?.lines || []).map((line) => [line.product, line]));
  const itemsCount = items.reduce((n, item) => n + item.quantity, 0);

  if (items.length === 0 && !orderSuccess) {
    return (
      <EmptyState
        icon={ShoppingBagIcon}
        title="Корзина пуста"
        text="Добавьте товары из каталога — и оформите заказ за пару минут."
        action={
          <Button to="/" size="lg" block className="max-w-xs">
            К товарам
          </Button>
        }
      />
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-2 pt-2 pb-24">
      <div className="space-y-8">
        {/* CART ITEMS */}
        <div className="space-y-3">
          {items.map((item) => (
            <CartItem
              key={item.id}
              item={item}
              price={quoteLines[item.id]?.price}
              oldPrice={quoteLines[item.id]?.old_price}
            />
          ))}
        </div>

        {/* CONTACT INFO */}
        <section>
          <SectionTitle icon={UserIcon}>Контактные данные</SectionTitle>
          <div className="space-y-4">
            <Input label="Имя" placeholder="Как к вам обращаться" autoComplete="name" {...fieldProps("name")} />
            <Input
              label="Номер телефона"
              type="tel"
              inputMode="tel"
              placeholder="+48 123 456 789"
              autoComplete="tel"
              {...fieldProps("phone")}
            />
            {!isLoggedIn && (
              <Input label="Telegram" placeholder="@username" {...fieldProps("tg_username")} />
            )}
            {selectedDelivery?.email_required && (
              <Input
                label="Email"
                type="email"
                inputMode="email"
                placeholder="you@example.com"
                autoComplete="email"
                {...fieldProps("email")}
              />
            )}
          </div>
        </section>

        {/* DELIVERY */}
        <section>
          <SectionTitle icon={TruckIcon}>Способ доставки</SectionTitle>

          <div data-error={!!errors.delivery_method} role="radiogroup" className="grid grid-cols-2 gap-3">
            {deliveryMethods.map((method) => {
              const selected = String(method.id) === formData.delivery_method;
              const price = Number(method.price);
              const isFreeNow = selected && quote && Number(quote.delivery_price) === 0;

              return (
                <OptionCard
                  key={method.id}
                  selected={selected}
                  error={errors.delivery_method}
                  onSelect={() => setField("delivery_method", String(method.id))}
                  className="text-center"
                >
                  <p className="font-semibold text-white">{method.name}</p>
                  {price > 0 &&
                    (isFreeNow ? (
                      <p className="text-sm mt-1">
                        <span className="text-slate-500 line-through mr-1">{formatZl(price)}</span>
                        <span className="font-semibold text-emerald-400">Бесплатно</span>
                      </p>
                    ) : (
                      <p className="text-sm mt-1 text-slate-300">{formatZl(price)}</p>
                    ))}
                  {price > 0 && Number(method.free_from) > 0 && (
                    <p className="mt-1 text-xs text-emerald-400/90">
                      Бесплатно от {formatZl(method.free_from)}
                    </p>
                  )}
                </OptionCard>
              );
            })}
          </div>
          {errors.delivery_method && (
            <p className="mt-2 text-sm text-red-400">{ERROR_TEXT.delivery_method}</p>
          )}

          {selectedDelivery?.address_required && (
            <div className="mt-5 space-y-4">
              {addresses.length > 0 && (
                <Select
                  label="Сохранённый адрес"
                  value={selectedSavedAddress}
                  onChange={(e) => {
                    setErrors((prev) => ({ ...prev, address: false }));
                    setSelectedSavedAddress(e.target.value);
                  }}
                >
                  <option value="">Выберите адрес</option>
                  {addresses.map((a) => (
                    <option key={a.id} value={a.address}>
                      {a.address}
                    </option>
                  ))}
                </Select>
              )}

              <Input
                label={addresses.length > 0 ? "Или новый адрес доставки" : "Адрес доставки"}
                placeholder="Улица, дом, квартира, город"
                autoComplete="street-address"
                {...fieldProps("address")}
              />
            </div>
          )}
        </section>

        {/* PAYMENT */}
        <section>
          <SectionTitle icon={CreditCardIcon}>Способ оплаты</SectionTitle>

          <div data-error={!!errors.payment_method} role="radiogroup" className="space-y-3">
            {/* Способи оплати, недоступні для обраної доставки, не показуємо взагалі */}
            {paymentMethods
              .filter((method) => isPaymentAllowed(method.id))
              .map((method) => {
                const selected = String(method.id) === formData.payment_method;

                return (
                  <OptionCard
                    key={method.id}
                    selected={selected}
                    error={errors.payment_method}
                    onSelect={() => setField("payment_method", String(method.id))}
                    className="flex justify-between items-center gap-3"
                  >
                    <span className="font-semibold text-white">{method.name}</span>
                    <RadioDot selected={selected} />
                  </OptionCard>
                );
              })}
          </div>
          {errors.payment_method && (
            <p className="mt-2 text-sm text-red-400">{ERROR_TEXT.payment_method}</p>
          )}
        </section>

        {/* COMMENT */}
        <TextArea
          label="Комментарий к заказу"
          placeholder="Например: удобное время доставки"
          hint="Необязательно"
          maxLength={500}
          {...fieldProps("comment")}
        />

        <OrderSummary
          quote={quote}
          itemsCount={itemsCount}
          subtotal={subtotal}
          discount={discount}
          deliveryPrice={deliveryPrice}
          totalPrice={totalPrice}
          selectedDelivery={selectedDelivery}
          promoInput={promoInput}
          setPromoInput={setPromoInput}
          appliedCode={appliedCode}
          setAppliedCode={setAppliedCode}
        />
      </div>

      {/* CHECKOUT BUTTON */}
      <div className="fixed bottom-0 left-0 right-0 p-4 pb-[calc(env(safe-area-inset-bottom)+16px)] bg-[#101622]/95 backdrop-blur-xl border-t border-slate-700 flex items-center gap-3 z-50">
        <Button
          variant="secondary"
          size="icon"
          aria-label="Назад"
          onClick={() => navigate(-1)}
          disabled={isSubmitting}
        >
          <ArrowLeftIcon className="w-5 h-5" />
        </Button>

        <Button size="lg" className="flex-1 !rounded-full" onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? "Отправка..." : `Подтвердить заказ · ${formatZl(totalPrice)}`}
          {!isSubmitting && <ChevronRightIcon className="w-5 h-5" />}
        </Button>
      </div>

      {orderSuccess && (
        <ResultModal
          title={`Заказ #${orderNumber} успешно оформлен!`}
          text="Мы получили ваш заказ. Наши менеджеры дерутся за возможность связаться с вами первыми!"
          actionLabel="На главную"
          onAction={() => {
            dispatch(resetOrderSuccess());
            navigate("/");
          }}
        />
      )}

      {orderError && (
        <ResultModal
          variant="error"
          title="Ошибка оформления"
          text="Возникла неизвестная ошибка. Обратитесь к нашему менеджеру со скриншотом этой ошибки, пожалуйста."
          actionLabel="Закрыть"
          onAction={() => dispatch(resetOrderSuccess())}
        >
          {/* Деталі помилки для скріншоту */}
          <div className="w-full bg-slate-800 rounded-xl p-3 mb-6 text-left">
            <p className="text-xs text-red-400 font-mono break-all">
              {typeof orderError === "string" ? orderError : JSON.stringify(orderError)}
            </p>
          </div>
        </ResultModal>
      )}
    </div>
  );
};

export default CartPage;
